import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import { StoryblokLink } from '../../../storyblok/storyblok-types';
import { ButtonDirective } from '../../directives/button.directive';
import { ResizeDirective } from '../../directives/resize.directive';
import { SvgComponent } from '../svg/svg.component';

@Component({
  selector: 'app-website-footer',
  templateUrl: './website-footer.component.html',
  styleUrls: ['./website-footer.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ResizeDirective, RouterLink, SvgComponent, ButtonDirective, AsyncPipe],
})
export class WebsiteFooterComponent {
  private route = inject(ActivatedRoute);
  @Input() dark: boolean;
  @Input() black: boolean;
  @Input() simpleMode: boolean;
  currentYear = new Date().getFullYear();

  private getUrl(link: StoryblokLink) {
    if (link.linktype === 'story') {
      return `/${link.cached_url}`;
    }
    return link.url;
  }

  footerData$ = this.route.data.pipe(map(data => data.footer?.content));

  footerMenu$ = this.footerData$.pipe(
    filter(Boolean),
    map(footer => {
      const menu = footer.groups.map(group => {
        const items = group.items.map(item => {
          const url = this.getUrl(item.link);
          return {
            ...item,
            link: url,
            isExternal: url.includes('://'),
          };
        });

        return {
          ...group,
          items,
        };
      });

      const groups = [];
      const columns = 4;

      menu.forEach((data, i) => {
        const index = i % columns;
        if (!groups[index]) {
          groups[index] = [];
        }
        groups[index].push(data);
      });

      return groups;
    })
  );

  logo$ = this.footerData$.pipe(
    map(footer => {
      return {
        light: footer.lightLogo,
        dark: footer.darkLogo,
      };
    })
  );

  legalLinks$ = this.footerData$.pipe(
    map(footer => {
      return footer.legalLinks.map(item => {
        return {
          ...item,
          link: this.getUrl(item.link),
        };
      });
    })
  );

  socialIcons$ = this.footerData$.pipe(
    map(footer => {
      return footer.socialIcons.map(item => {
        return {
          ...item,
          link: this.getUrl(item.link),
        };
      });
    })
  );

  tags$ = this.footerData$.pipe(map(footer => footer.tags));
}
